import { DollarOutlined } from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { EXCHANGE_RATE } from "../../constants/common";
import {
  IResearchPurchaseRequest,
  IUpdateListMaterial,
  IUpdateResearchPurchaseRequest,
} from "../../models/Research";
import { notify } from "../../utils/toast";
import { PriorityMark } from "../../enum/common";
import { ISuppliesLogistics } from "../../models/Logistics";
import { useSearchSupplierLogistics } from "../../query/LogisticsQuery";
import { debounce } from "lodash";

const { TextArea } = Input;
const { Text } = Typography;

interface MaterialWithCalculated extends IUpdateListMaterial {
  unitPriceExwUsd: number;
  totalPriceExwUsd: number;
  totalPriceVnd: number;
  grandTotalVnd: number;
}

interface DrawerEditPurchaseRequestProps {
  open: boolean;
  onClose: () => void;
  purchaseRequest: IResearchPurchaseRequest | null;
  onSubmit: (id: number, data: IUpdateResearchPurchaseRequest) => void;
  loading?: boolean;
}

const DrawerEditPurchaseRequest = ({
  open,
  onClose,
  purchaseRequest,
  onSubmit,
  loading = false,
}: DrawerEditPurchaseRequestProps) => {
  const [form] = Form.useForm();
  const [searchCode, setSearchCode] = useState<string>("");
  const [materials, setMaterials] = useState<MaterialWithCalculated[]>([]);
  const [exchangeRate, setExchangeRate] = useState<number>(EXCHANGE_RATE);
  const [supplierData, setSupplierData] = useState<ISuppliesLogistics | null>(
    null,
  );

  const { data: searchResult } = useSearchSupplierLogistics(searchCode);

  useEffect(() => {
    if (searchResult) {
      setSupplierData(searchResult);
    }
  }, [searchResult]);
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (value && value.length >= 2) {
        setSearchCode(value);
      } else {
        setSearchCode("");
      }
    }, 300),
    [],
  );
  const handleInternalCodeChange = (value: string) => {
    debouncedSearch(value);
  };

  const handleInternalCodeBlur = (id: number) => {
    const material = materials.find((m) => m.id === id);
    if (!material || !material.internalCode) return;

    if (supplierData && supplierData.internalCode === material.internalCode) {
      setMaterials(
        materials.map((m) =>
          m.id === id
            ? {
                ...m,
                internalCode: supplierData.internalCode,
                partNumber: supplierData.partNumber,
                manufacturer: supplierData.manufacturer,
                description: supplierData.description,
                unit: supplierData.unit,
                importTax: supplierData.importTax,
              }
            : m,
        ),
      );
      handleMaterialChange(id, "importTax", supplierData.importTax);
      notify.success("Đã cập nhật thông tin vật tư từ mã nội bộ");
    }
  };

  useEffect(() => {
    if (purchaseRequest && open) {
      form.setFieldsValue({
        requestBy: purchaseRequest.requestBy,
        exchangeRate: purchaseRequest.exchangeRate,
        priority: purchaseRequest.priority,
        note: purchaseRequest.note,
      });
      const rate = purchaseRequest.exchangeRate;
      setExchangeRate(rate);

      if (purchaseRequest.listMaterials) {
        setMaterials(
          purchaseRequest.listMaterials.map((m) => {
            const qty = Number(m.quantity);
            const unitPriceVnd = Number(m.unitPriceVnd);
            const shippingVnd = Number(m.shippingVnd);
            const importTax = Number(m.importTax || 0);
            const unitPriceExwUsd = Number((unitPriceVnd / rate).toFixed(2));
            const totalPriceVnd = qty * unitPriceVnd;
            const totalPriceExwUsd = Number((totalPriceVnd / rate).toFixed(2));
            const totalImportTax = (totalPriceVnd * importTax) / 100;
            const grandTotalVnd = totalPriceVnd + shippingVnd + totalImportTax;
            return {
              id: m.id,
              partNumber: m.partNumber,
              internalCode: m.internalCode,
              category: m.category,
              manufacturer: m.manufacturer,
              description: m.description,
              unit: m.unit,
              quantity: qty,
              source: m.source,
              datePr: m.datePr,
              crd: m.crd,
              unitPriceVnd,
              shippingVnd,
              link: m.link,
              unitPriceExwUsd,
              totalPriceExwUsd,
              totalPriceVnd,
              grandTotalVnd,
              importTax,
            };
          }),
        );
      }
    }
  }, [purchaseRequest, open, form]);

  const handleMaterialChange = (
    id: number,
    field: keyof MaterialWithCalculated,
    value: any,
  ) => {
    setMaterials(
      materials.map((m) => {
        if (m.id === id) {
          const updated = { ...m, [field]: value };
          if (
            field === "quantity" ||
            field === "unitPriceVnd" ||
            field === "shippingVnd" ||
            field === "importTax"
          ) {
            const qty = field === "quantity" ? value : updated.quantity;
            const unitPrice =
              field === "unitPriceVnd" ? value : updated.unitPriceVnd;
            const shipping =
              field === "shippingVnd" ? value : updated.shippingVnd;
            const importTax = field === "importTax" ? value : updated.importTax;

            updated.unitPriceExwUsd = Number(
              (unitPrice / exchangeRate).toFixed(2),
            );
            updated.totalPriceVnd = qty * unitPrice;
            updated.totalPriceExwUsd = Number(
              (updated.totalPriceVnd / exchangeRate).toFixed(2),
            );
            const totalImportTax = (updated.totalPriceVnd * importTax) / 100;
            updated.grandTotalVnd =
              updated.totalPriceVnd + shipping + totalImportTax;
          }

          return updated;
        }
        return m;
      }),
    );
  };

  const handleExchangeRateChange = (newRate: number) => {
    setExchangeRate(newRate);
    setMaterials(
      materials.map((m) => ({
        ...m,
        unitPriceExwUsd: Number((m.unitPriceVnd / newRate).toFixed(2)),
        totalPriceExwUsd: Number(
          ((m.quantity * m.unitPriceVnd) / newRate).toFixed(2),
        ),
      })),
    );
  };

  const validateMaterials = () => {
    const requiredFields = [
      "partNumber",
      "category",
      "manufacturer",
      "description",
      "source",
      "datePr",
      "crd",
      "unitPriceVnd",
      "quantity",
      "shippingVnd",
    ];

    for (const material of materials) {
      for (const field of requiredFields) {
        if (!material[field as keyof MaterialWithCalculated]) {
          return false;
        }
      }
    }
    return true;
  };

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (materials.length === 0) {
          notify.error("Vui lòng thêm ít nhất một vật tư");
          return;
        }
        if (!validateMaterials()) {
          notify.error("Vui lòng điền đầy đủ thông tin vật tư");
          return;
        }

        const listMaterials: IUpdateListMaterial[] = materials.map((m) => ({
          id: m.id,
          partNumber: m.partNumber,
          internalCode: m.internalCode,
          category: m.category,
          manufacturer: m.manufacturer,
          description: m.description,
          unit: m.unit,
          quantity: m.quantity,
          source: m.source,
          datePr: m.datePr,
          crd: m.crd,
          unitPriceVnd: m.unitPriceVnd,
          shippingVnd: m.shippingVnd,
          link: m.link,
          importTax: m.importTax,
        }));

        const data: IUpdateResearchPurchaseRequest = {
          exchangeRate: values.exchangeRate,
          requestBy: values.requestBy,
          note: values.note || "",
          priority: values.priority,
          listMaterials,
        };
        onSubmit(purchaseRequest!.id, data);
      })
      .catch(() => {
        notify.error("Vui lòng kiểm tra lại thông tin các trường bắt buộc");
      });
  };

  const handleClose = () => {
    form.resetFields();
    setMaterials([]);
    onClose();
  };

  const columns = [
    {
      title: "Mã nội bộ",
      dataIndex: "internalCode",
      key: "internalCode",
      width: 120,
      render: (_: any, record: MaterialWithCalculated) => (
        <Input
          value={record.internalCode || ""}
          onChange={(e) => {
            handleMaterialChange(
              record.id,
              "internalCode",
              e.target.value || null,
            );
            handleInternalCodeChange(e.target.value);
          }}
          onBlur={() => handleInternalCodeBlur(record.id)}
          placeholder="Mã nội bộ"
        />
      ),
    },
    {
      title: "Part Number *",
      dataIndex: "partNumber",
      key: "partNumber",
      width: 150,
      fixed: "left" as const,
      render: (_: any, record: MaterialWithCalculated) => (
        <Input
          value={record.partNumber}
          onChange={(e) =>
            handleMaterialChange(record.id, "partNumber", e.target.value)
          }
          disabled
          placeholder="Part Number"
          status={!record.partNumber ? "error" : ""}
        />
      ),
    },

    {
      title: "Danh mục *",
      dataIndex: "category",
      key: "category",
      width: 120,
      render: (_: any, record: MaterialWithCalculated) => (
        <Input
          value={record.category}
          onChange={(e) =>
            handleMaterialChange(record.id, "category", e.target.value)
          }
          placeholder="Danh mục"
          status={!record.category ? "error" : ""}
        />
      ),
    },
    {
      title: "Nhà sản xuất *",
      dataIndex: "manufacturer",
      key: "manufacturer",
      width: 120,
      render: (_: any, record: MaterialWithCalculated) => (
        <Input
          value={record.manufacturer}
          onChange={(e) =>
            handleMaterialChange(record.id, "manufacturer", e.target.value)
          }
          disabled
          placeholder="Nhà sản xuất"
          status={!record.manufacturer ? "error" : ""}
        />
      ),
    },
    {
      title: "Mô tả *",
      dataIndex: "description",
      key: "description",
      width: 200,
      render: (_: any, record: MaterialWithCalculated) => (
        <Input
          value={record.description}
          onChange={(e) =>
            handleMaterialChange(record.id, "description", e.target.value)
          }
          disabled
          placeholder="Mô tả"
          status={!record.description ? "error" : ""}
        />
      ),
    },
    {
      title: "SL *",
      dataIndex: "quantity",
      key: "quantity",
      width: 80,
      render: (_: any, record: MaterialWithCalculated) => (
        <InputNumber
          value={record.quantity}
          onChange={(value) =>
            handleMaterialChange(record.id, "quantity", value || 1)
          }
          min={1}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Đơn vị *",
      dataIndex: "unit",
      key: "unit",
      width: 80,
      render: (_: any, record: MaterialWithCalculated) => (
        <Input
          value={record.unit}
          onChange={(e) =>
            handleMaterialChange(record.id, "unit", e.target.value)
          }
          disabled
          placeholder="pcs"
        />
      ),
    },
    {
      title: "Nguồn *",
      dataIndex: "source",
      key: "source",
      width: 120,
      render: (_: any, record: MaterialWithCalculated) => (
        <Input
          value={record.source}
          onChange={(e) =>
            handleMaterialChange(record.id, "source", e.target.value)
          }
          placeholder="Nguồn"
          status={!record.source ? "error" : ""}
        />
      ),
    },
    {
      title: "Ngày PR *",
      dataIndex: "datePr",
      key: "datePr",
      width: 130,
      render: (_: any, record: MaterialWithCalculated) => (
        <DatePicker
          value={record.datePr ? dayjs(record.datePr) : null}
          onChange={(date) =>
            handleMaterialChange(
              record.id,
              "datePr",
              date ? date.format("YYYY-MM-DD") : "",
            )
          }
          format="DD/MM/YYYY"
          style={{ width: "100%" }}
          status={!record.datePr ? "error" : ""}
        />
      ),
    },
    {
      title: "CRD *",
      dataIndex: "crd",
      key: "crd",
      width: 130,
      render: (_: any, record: MaterialWithCalculated) => (
        <DatePicker
          value={record.crd ? dayjs(record.crd) : null}
          onChange={(date) =>
            handleMaterialChange(
              record.id,
              "crd",
              date ? date.format("YYYY-MM-DD") : "",
            )
          }
          format="DD/MM/YYYY"
          style={{ width: "100%" }}
          status={!record.crd ? "error" : ""}
        />
      ),
    },
    {
      title: "Đơn giá VND *",
      dataIndex: "unitPriceVnd",
      key: "unitPriceVnd",
      width: 120,
      render: (_: any, record: MaterialWithCalculated) => (
        <InputNumber
          value={record.unitPriceVnd}
          onChange={(value) =>
            handleMaterialChange(record.id, "unitPriceVnd", value || 0)
          }
          min={0}
          style={{ width: "100%" }}
          formatter={(value) =>
            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
          }
        />
      ),
    },
    {
      title: "Đơn giá USD",
      dataIndex: "unitPriceExwUsd",
      key: "unitPriceExwUsd",
      width: 120,
      render: (_: any, record: MaterialWithCalculated) => (
        <Text type="secondary">${record.unitPriceExwUsd.toLocaleString()}</Text>
      ),
    },
    {
      title: "Tổng VND",
      dataIndex: "totalPriceVnd",
      key: "totalPriceVnd",
      width: 130,
      render: (_: any, record: MaterialWithCalculated) => (
        <Text strong>{record.totalPriceVnd.toLocaleString()} VNĐ</Text>
      ),
    },
    {
      title: "Tổng USD",
      dataIndex: "totalPriceExwUsd",
      key: "totalPriceExwUsd",
      width: 120,
      render: (_: any, record: MaterialWithCalculated) => (
        <Text type="secondary">
          ${record.totalPriceExwUsd.toLocaleString()}
        </Text>
      ),
    },
    {
      title: "Phí ship VNĐ *",
      dataIndex: "shippingVnd",
      key: "shippingVnd",
      width: 120,
      render: (_: any, record: MaterialWithCalculated) => (
        <InputNumber
          value={record.shippingVnd}
          onChange={(value) =>
            handleMaterialChange(record.id, "shippingVnd", value || 0)
          }
          min={0}
          style={{ width: "100%" }}
          formatter={(value) =>
            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
          }
        />
      ),
    },
    {
      title: "Thuế NK (%)",
      dataIndex: "importTax",
      key: "importTax",
      width: 100,
      render: (_: any, record: MaterialWithCalculated) => (
        <InputNumber
          value={record.importTax}
          onChange={(value) =>
            handleMaterialChange(record.id, "importTax", value || 0)
          }
          min={0}
          max={100}
          disabled
          style={{ width: "100%" }}
          formatter={(value) => `${value}%`}
          parser={(value) => Number(value!.replace("%", ""))}
        />
      ),
    },
    {
      title: "Tổng thuế NK",
      key: "totalImportTax",
      width: 120,
      render: (_: any, record: MaterialWithCalculated) => {
        const totalImportTax = (record.totalPriceVnd * record.importTax) / 100;
        return (
          <Text className="text-orange-600">
            {totalImportTax.toLocaleString("vi-VN")}
          </Text>
        );
      },
    },
    {
      title: "Tổng cộng",
      dataIndex: "grandTotalVnd",
      key: "grandTotalVnd",
      width: 140,
      render: (_: any, record: MaterialWithCalculated) => (
        <div>
          <Text type="secondary" style={{ fontSize: "11px", display: "block" }}>
            ${record.totalPriceExwUsd.toLocaleString()}
          </Text>
          <Text strong style={{ fontSize: "14px", color: "#1890ff" }}>
            {record.grandTotalVnd.toLocaleString()} VNĐ
          </Text>
        </div>
      ),
    },
    {
      title: "Link *",
      dataIndex: "link",
      key: "link",
      width: 150,
      render: (_: any, record: MaterialWithCalculated) => {
        return (
          <Input
            value={record.link}
            onChange={(e) =>
              handleMaterialChange(record.id, "link", e.target.value)
            }
          />
        );
      },
    },
  ];

  const totalProposalVnd = materials.reduce(
    (sum, m) => sum + m.grandTotalVnd,
    0,
  );

  return (
    <Drawer
      title="Chỉnh sửa đề xuất mua hàng"
      open={open}
      onClose={handleClose}
      width="100%"
      extra={
        <Space>
          <Button onClick={handleClose}>Hủy</Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={loading}
            disabled={materials.length === 0}
          >
            Cập nhật
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <Form.Item
            label="Người đề xuất"
            name="requestBy"
            rules={[{ required: true, message: "Vui lòng nhập người đề xuất" }]}
          >
            <Input placeholder="Tên người đề xuất" />
          </Form.Item>

          <Form.Item
            label="Tỉ giá ngoại tệ"
            name="exchangeRate"
            rules={[{ required: true, message: "Vui lòng nhập tỉ giá" }]}
          >
            <InputNumber
              className="w-full"
              placeholder="Tỉ giá ngoại tệ"
              onChange={(value) =>
                handleExchangeRateChange(Number(value) || EXCHANGE_RATE)
              }
            />
          </Form.Item>
          <Form.Item
            label="Mức độ yêu cầu"
            name="priority"
            rules={[
              { required: true, message: "Vui lòng chọn mức độ ưu tiên" },
            ]}
          >
            <Select placeholder="Chọn mức độ ưu tiên">
              <Select.Option value={PriorityMark.LOW}>Thường</Select.Option>
              <Select.Option value={PriorityMark.MEDIUM}>Gấp</Select.Option>
              <Select.Option value={PriorityMark.HIGH}>Rất gấp</Select.Option>
            </Select>
          </Form.Item>
        </div>

        <Form.Item label="Ghi chú" name="note">
          <TextArea rows={3} placeholder="Ghi chú cho đề xuất" />
        </Form.Item>
      </Form>

      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <Text strong>
            Danh sách vật tư ({materials.length} món) - Các trường có dấu * là
            bắt buộc
          </Text>
        </div>

        <Table
          dataSource={materials}
          columns={columns}
          rowKey="id"
          pagination={false}
          scroll={{ x: 2200, y: 400 }}
          size="small"
          bordered
          locale={{ emptyText: "Chưa có vật tư nào" }}
        />
      </div>

      <div className="bg-blue-50 dark:bg-gray-800 p-4 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DollarOutlined className="text-blue-500 text-xl" />
            <Text strong className="text-lg">
              Tổng đề xuất:
            </Text>
          </div>
          <Text strong className="text-2xl text-blue-600">
            {totalProposalVnd.toLocaleString()} VNĐ
          </Text>
        </div>
      </div>
    </Drawer>
  );
};

export default DrawerEditPurchaseRequest;
